import React from "react";

import moment from "moment";

import { MeetingSelect } from "~/common";
import { type MeetingEvent, UNSCHEDULED_MEETING } from "~/common/MeetingSelect";
import ViewportContext from "~/common/ViewportContext";
import { Button, useModals } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";

import NoteTimeSelectorModal from "./NoteTimeSelectorModal";

import type { NoteTimeSelectorModal_note$key } from "~/__generated__/NoteTimeSelectorModal_note.graphql";

import styles from "./css/NoteAndMeetingLinkerModal.module.css";

interface NoteAndMeetingLinkerModalProps {
	closeModal: () => void;
	currentEvent?: MeetingEvent;
	note: NoteTimeSelectorModal_note$key;
	onChangeLinkedMeeting: (event: MeetingEvent) => void;
	onChangeDateTime?: (startDateTime: string, endDateTime: string) => void;
	otherUserId?: string;
}

export default function NoteAndMeetingLinkerModal({
	closeModal,
	currentEvent,
	note: noteKey,
	onChangeLinkedMeeting,
	onChangeDateTime,
	otherUserId,
}: NoteAndMeetingLinkerModalProps) {
	const modals = useModals();
	const { isMobile } = React.useContext(ViewportContext);
	const [eventPickerValue, setEventPickerValue] = React.useState<MeetingEvent | undefined>(currentEvent);

	const noteStartTime = React.useMemo(() => moment(currentEvent?.start || ""), [currentEvent]);

	const backToDateTimeModal = React.useCallback(() => {
		if (!onChangeDateTime) return;

		modals.open(({ close }) => (
			<NoteTimeSelectorModal closeModal={close} note={noteKey} onChangeLinkedMeeting={onChangeLinkedMeeting} />
		));
		closeModal();
	}, [onChangeDateTime, modals, closeModal, noteKey, onChangeLinkedMeeting]);

	const applyChanges = React.useCallback(() => {
		if (eventPickerValue) onChangeLinkedMeeting(eventPickerValue);
		closeModal();
	}, [eventPickerValue, onChangeLinkedMeeting, closeModal]);

	const onEventPickerChange = React.useCallback(
		(selected: MeetingEvent | readonly MeetingEvent[] | null | undefined) => {
			if (!selected) return;
			const event = selected as MeetingEvent;
			setEventPickerValue(event);
		},
		[],
	);

	return (
		<Modal onClose={closeModal} width="40.625rem">
			<Modal.Header onClose={closeModal} noBottomBorder>
				<Modal.Title>Link note to meeting</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<div>
					<MeetingSelect
						menuPlacement={isMobile ? "top" : "auto"}
						useFeedbackStyle={false}
						upcomingOption
						onlyNoteless
						onChange={onEventPickerChange}
						oneOnOne={otherUserId}
						value={eventPickerValue}
						start={
							noteStartTime.isValid()
								? moment().subtract(6, "months").startOf("day").toDate()
								: moment().subtract(2, "weeks").startOf("day").toDate()
						}
						end={moment().add(3, "months").toDate()}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				{(!currentEvent || currentEvent.guid === UNSCHEDULED_MEETING.guid) && onChangeDateTime && (
					<Modal.FooterSecondaryActions>
						<Button variant="secondary" onClick={backToDateTimeModal}>
							Change date & time
						</Button>
					</Modal.FooterSecondaryActions>
				)}
				<Button variant="primary" size="normal" disabled={!Boolean(eventPickerValue)} onClick={applyChanges}>
					Apply changes
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
